// components/mobile/MobileActionsList.jsx
import React from 'react';
import { Alert, Button, Empty, Input, Segmented, Spin, Tag } from 'antd';
import { SearchOutlined, RightOutlined, CarOutlined } from '@ant-design/icons';
import { ProductThumb } from './ProductThumb';
import { getPresentation, integer, PERIODS } from '../../utils/mobileHelpers';
import { DeliveryPerformanceChart } from '../../MobileCharts';

export const MobileActionsList = ({ 
  sectionKey, section, period, search, draftSearch, setDraftSearch, 
  stockStatus, updateQuery, listState, domain, openDetail, 
  hasMore, page, summaryText, listTitle, searchPlaceholder, activeNavigation 
}) => {
  const meta = listState.meta || {};
  
  const getSectionIcon = (iconName) => {
    const icons = {
      'ShoppingCartOutlined': require('@ant-design/icons').ShoppingCartOutlined,
      'CarOutlined': CarOutlined,
      'DollarOutlined': require('@ant-design/icons').DollarOutlined,
      'SwapOutlined': require('@ant-design/icons').SwapOutlined,
      'TeamOutlined': require('@ant-design/icons').TeamOutlined,
      'InboxOutlined': require('@ant-design/icons').InboxOutlined,
      'UnorderedListOutlined': require('@ant-design/icons').UnorderedListOutlined,
    };
    return icons[iconName] || null;
  };

  const SectionIcon = getSectionIcon(section.icon);
  
  return (
    <>
      <header className="mobile-action-header">
        <div>
          <span>NDOÉ MOBILE · CONSULTATION</span>
          <h1>{section.title}</h1>
          <p>{section.subtitle}</p>
        </div>
      </header>
      
      {!section.snapshot && (
        <div className="mobile-action-periods" role="tablist">
          {PERIODS.map((item) => (
            <button 
              key={item.key} 
              type="button" 
              role="tab" 
              aria-selected={period === item.key} 
              className={period === item.key ? 'is-active' : ''} 
              onClick={() => updateQuery({ period: item.key }, { resetPage: true })}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
      
      {section.snapshot && (
        <div className="mobile-action-snapshot">
          <CarOutlined /> Situation actuelle : cette vue ne dépend pas de la période. Elle affiche les paires avec départ prouvé, sans vente confirmée ni retour enregistré.
        </div>
      )}
      
      {sectionKey === 'stock' && (
        <Segmented 
          className="mobile-action-stock-filter" 
          value={stockStatus} 
          onChange={(value) => updateQuery({ status: value }, { resetPage: true })} 
          options={[
            { label: 'À surveiller', value: 'alert' }, 
            { label: 'Tous les stocks', value: 'all' }, 
            { label: 'Ruptures', value: 'out' }
          ]} 
        />
      )}
      
      <form className="mobile-action-search" onSubmit={(event) => { event.preventDefault(); updateQuery({ q: draftSearch.trim() }, { resetPage: true }); }}>
        <Input 
          value={draftSearch} 
          onChange={(event) => setDraftSearch(event.target.value)} 
          allowClear 
          prefix={<SearchOutlined />} 
          placeholder={searchPlaceholder} 
          onClear={() => updateQuery({ q: '' }, { resetPage: true })} 
        />
        <Button htmlType="submit" type="primary">Rechercher</Button>
      </form>
      
      {listState.error && <Alert className="mobile-action-alert" showIcon type="warning" message={listState.error} />}
      
      {listState.loading ? (
        <div className="mobile-action-loading">
          <Spin size="large" />
          <span>Chargement de la vue mobile…</span>
        </div>
      ) : (
        <>
          <section className={`mobile-action-hero ${section.tone}`}>
            <span className="mobile-action-hero-icon">
              {SectionIcon && <SectionIcon />}
            </span>
            <div>
              <small>APERÇU</small>
              <strong>{integer(meta.total)}</strong>
              <p>{summaryText}</p>
            </div>
          </section>
          
          {sectionKey === 'livreurs' && <DeliveryPerformanceChart items={listState.items} />}
          
          <section className="mobile-action-list-panel">
            <div className="mobile-action-list-heading">
              <div>
                <span>LISTE DÉTAILLÉE</span>
                <h2>{listTitle}</h2>
              </div>
              <Tag>{integer(meta.total)}</Tag>
            </div>
            
            {listState.items.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucun élément ne correspond à cette vue." />
            ) : (
              <div className="mobile-action-result-list">
                {listState.items.map((item) => {
                  const presentation = getPresentation(sectionKey, item);
                  return (
                    <button 
                      type="button" 
                      key={`${item.detail_type}:${item.detail_id}`} 
                      className="mobile-action-result" 
                      disabled={!presentation.navigable} 
                      onClick={() => presentation.navigable && openDetail(item.detail_type, item.detail_id)}
                    >
                      <ProductThumb domain={domain} image={presentation.image} title={presentation.title} />
                      <span className="mobile-action-result-copy">
                        <small>{presentation.eyebrow}</small>
                        <strong>{presentation.title}</strong>
                        <em>{presentation.subtitle}</em>
                        <i>{presentation.date}</i>
                      </span>
                      <span className="mobile-action-result-side">
                        <b>{presentation.note}</b>
                        {presentation.navigable && <RightOutlined />}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            
            {hasMore && (
              <div className="mobile-action-more">
                <Button loading={listState.loadingMore} onClick={() => updateQuery({ page: page + 1 })}>
                  Charger plus
                </Button>
              </div>
            )}
          </section>
        </>
      )}
    </>
  );
};